import { useNavigate } from 'react-router-dom';
import { Breadcrumb } from '../ui/breadcrumb';
import { ArrowLeft, BookOpen, Crown, Zap, Clock, Shield, Star, Repeat } from 'lucide-react';
import { motion } from 'framer-motion';

export function CustomerWork() {
  const navigate = useNavigate();

  const breadcrumbItems = [
    { label: 'Learn & Earn', href: '/learn' },
    { label: 'Customer Work' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-orange-100 to-red-100 px-4 py-6 sm:p-8 relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-orange-300/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-red-300/30 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 sm:w-80 sm:h-80 bg-orange-200/30 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto relative z-10">

        {/* Breadcrumb + Back */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="pt-20 sm:pt-8"
        >
          <div className="hidden sm:block">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <button
            onClick={() => navigate('/learn')}
            className="mb-6 flex items-center gap-2 px-4 py-2 rounded-xl bg-white/70 hover:bg-white border border-orange-200 hover:border-orange-400 text-orange-700 hover:text-orange-900 font-semibold text-sm shadow-sm hover:shadow-md transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portals
          </button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-300 text-orange-700 px-4 sm:px-6 py-2 rounded-full mb-4">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm font-semibold">Customer Work Portal</span>
          </div>
          <h1 className="mb-4 text-orange-700 text-3xl sm:text-4xl md:text-5xl font-bold">
            Choose Your Service
          </h1>
          <p className="text-orange-600 text-base sm:text-lg max-w-2xl mx-auto px-2">
            Select the service type that best fits your needs and budget
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 max-w-4xl mx-auto">

          {/* ── Subscription Service Card (Violet) ── */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -4 }}
            onClick={() => navigate('/learn/customer-work/subscription')}
            className="cursor-pointer rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-violet-50 to-purple-100 border-2 border-violet-200 hover:border-violet-400 group relative"
          >
            {/* Card top accent */}
            <div className="h-2 bg-gradient-to-r from-violet-500 to-purple-600"></div>

            {/* Glow blob */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-violet-300/30 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500 pointer-events-none"></div>

            <div className="p-5 sm:p-8">
              {/* Icon */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 sm:mb-5 mx-auto shadow-lg group-hover:rotate-6 transition-transform duration-300">
                <Crown className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>

              <h2 className="text-center text-xl sm:text-2xl font-bold text-violet-800 mb-1">
                Subscription Service
              </h2>
              <p className="text-center text-violet-600 text-sm mb-4 sm:mb-6">
                Premium service with ongoing support
              </p>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {[
                  'Monthly subscription plans',
                  'Priority support & faster delivery',
                  'Unlimited revisions',
                  'Dedicated account manager',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-white/60 rounded-xl border border-violet-100">
                    <div className="w-2 h-2 bg-violet-500 rounded-full flex-shrink-0"></div>
                    <span className="text-xs sm:text-sm text-violet-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <button className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-xl font-bold shadow-lg hover:shadow-violet-300 transition-all duration-200">
                  Get Started →
                </button>
              </div>
            </div>
          </motion.div>

          {/* ── OTS Service Card (Orange) ── */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.02, y: -4 }}
            onClick={() => navigate('/learn/customer-work/ots')}
            className="cursor-pointer rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-orange-50 to-red-100 border-2 border-orange-200 hover:border-orange-400 group relative"
          >
            {/* Card top accent */}
            <div className="h-2 bg-gradient-to-r from-orange-500 to-red-500"></div>

            {/* Glow blob */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-300/30 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500 pointer-events-none"></div>

            <div className="p-5 sm:p-8">
              {/* Icon */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-4 sm:mb-5 mx-auto shadow-lg group-hover:rotate-6 transition-transform duration-300">
                <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>

              <h2 className="text-center text-xl sm:text-2xl font-bold text-orange-800 mb-1">
                OTS Service
              </h2>
              <p className="text-center text-orange-600 text-sm mb-4 sm:mb-6">
                One Time Service for immediate needs
              </p>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {[
                  'Pay per project',
                  'Quick turnaround time',
                  'Standard quality assurance',
                  'Direct communication',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-white/60 rounded-xl border border-orange-100">
                    <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
                    <span className="text-xs sm:text-sm text-orange-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <button className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl font-bold shadow-lg hover:shadow-orange-300 transition-all duration-200">
                  Get Started →
                </button>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
}
